import React from "react";
import api from "@services/api";
import Loader from "@components/Loader";
import "@styles/opencart-admin.css";

// WN-0239 — Time Slot report (standalone, under Sales).
// For the selected date it shows how busy each slot is: pickups, deliveries and
// the combined total, by order type, a load-bar view, a slot x type matrix, a
// next-7-days heatmap, and a per-number drill-down. Data from
// /reports/time-slot-report (+ /orders for drill-down).

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const SHOW = [
  { key: "both", label: "Both" },
  { key: "pickup", label: "Pickup only" },
  { key: "delivery", label: "Delivery only" },
];

const PICK = "#2563eb"; // pickups
const DROP = "#16a34a"; // deliveries

// WN-0239 c#539: a solid, consistent color per marketplace for the prominent
// total cards (blue WashyWash, orange FlashWash, extra palette for others).
const MP_COLORS = { WashyWash: "#2563eb", FlashWash: "#ea580c" };
const MP_PALETTE = ["#7c3aed", "#0891b2", "#be185d", "#15803d"];
function mpColor(name) {
  if (MP_COLORS[name]) return MP_COLORS[name];
  if (name === "No marketplace") return "#6b7280";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % MP_PALETTE.length;
  return MP_PALETTE[h];
}

// Light -> dark scale for the heatmap, relative to the row/grid max.
const heatColor = (v, max) => {
  if (!v) return "#f3f4f6";
  const t = Math.min(1, v / (max || 1));
  // blend from pale blue to deep blue
  const r = Math.round(219 - t * (219 - 30));
  const g = Math.round(234 - t * (234 - 64));
  const b = Math.round(254 - t * (254 - 175));
  return `rgb(${r},${g},${b})`;
};

function useIsMobile(bp = 640) {
  const [m, setM] = React.useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
  React.useEffect(() => {
    const on = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return m;
}

export default function TimeSlotReportPage() {
  const isMobile = useIsMobile();
  const [date, setDate] = React.useState(todayStr());
  const [storeId, setStoreId] = React.useState(""); // "" = All
  const [marketplace, setMarketplace] = React.useState("all");
  const [show, setShow] = React.useState("both");

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [expanded, setExpanded] = React.useState(null); // slot presentation expanded in View C
  const [expandedUser, setExpandedUser] = React.useState(null); // user row expanded in Reschedules-per-user

  // Drill-down modal state.
  const [drill, setDrill] = React.useState(null); // {slot, leg, type, label}
  const [drillData, setDrillData] = React.useState(null);
  const [drillLoading, setDrillLoading] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    setErr("");
    const params = { date, show, heatmap: 1 };
    if (storeId !== "") params.store_id = storeId;
    if (marketplace !== "all") params.marketplace = marketplace;
    api
      .get("/reports/time-slot-report", { params })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.message || "Could not load the report."))
      .finally(() => setLoading(false));
  }, [date, show, storeId, marketplace]);

  React.useEffect(() => {
    load();
    setExpanded(null);
  }, [load]);

  const openDrill = (slot, leg, type, label) => {
    if (slot?.is_no_slot && slot?.presentation) slot = slot.presentation;
    const slotName = typeof slot === "string" ? slot : slot.presentation;
    setDrill({ slot: slotName, leg, type, label });
    setDrillData(null);
    setDrillLoading(true);
    const params = { date, slot: slotName, leg };
    if (storeId !== "") params.store_id = storeId;
    if (type) params.type = type;
    api
      .get("/reports/time-slot-report/orders", { params })
      .then((r) => setDrillData(r.data))
      .catch(() => setDrillData({ orders: [], count: 0 }))
      .finally(() => setDrillLoading(false));
  };

  const slots = data?.slots || [];
  const types = data?.order_types || [];
  const totals = data?.totals || { pickups: 0, deliveries: 0, total: 0 };
  const maxTotal = Math.max(1, ...slots.map((s) => s.total));
  const wantP = show !== "delivery";
  const wantD = show !== "pickup";

  // Group the slot summary by marketplace (WN-0239 c#534): regular WashyWash slots,
  // then FlashWash etc., each with its own subtotal; a grand total at the bottom.
  const mpGroups = React.useMemo(() => {
    const order = [];
    const map = {};
    slots.forEach((s) => {
      const k = s.marketplace || "No marketplace";
      if (!map[k]) { map[k] = { name: k, rows: [], p: 0, d: 0, t: 0 }; order.push(k); }
      map[k].rows.push(s);
      map[k].p += s.pickups; map[k].d += s.deliveries; map[k].t += s.total;
    });
    return order.map((k) => map[k]);
  }, [slots]);
  const multiMp = mpGroups.length > 1;

  const numBtn = (val, onClick, strong) => (
    <button
      type="button"
      onClick={val ? onClick : undefined}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: val ? "pointer" : "default",
        color: val ? "#111827" : "#9ca3af",
        fontWeight: strong ? 700 : 500,
        textDecoration: val ? "underline" : "none",
        textDecorationColor: "#d1d5db",
        fontSize: "inherit",
      }}
    >
      {val}
    </button>
  );

  return (
    <div style={{ padding: isMobile ? "12px" : "16px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: isMobile ? 19 : 22 }}>Time Slot report</h2>
      <p style={{ margin: "0 0 10px", color: "#6b7280", fontSize: 13 }}>
        Pickups and deliveries per time slot for the selected day, with order types.
      </p>
      {data?.rule && (
        <div style={{ fontSize: 12, color: "#6b7280", background: "#f9fafb", border: "1px solid #eef2f7", borderRadius: 8, padding: "8px 10px", marginBottom: 14 }}>
          <b style={{ color: "#374151" }}>Counting rule:</b> {data.rule}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", marginBottom: 18 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12, color: "#374151", flex: isMobile ? "1 1 46%" : "0 0 auto" }}>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            style={{ padding: "8px 9px", border: "1px solid #d1d5db", borderRadius: 6, marginTop: 3, width: "100%" }} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12, color: "#374151", flex: isMobile ? "1 1 46%" : "0 0 auto" }}>
          Store
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)}
            style={{ padding: "8px 9px", border: "1px solid #d1d5db", borderRadius: 6, marginTop: 3, width: "100%", minWidth: isMobile ? 0 : 170 }}>
            {(data?.stores || [{ store_id: null, name: "All stores" }]).map((s, i) => (
              <option key={i} value={s.store_id === null ? "" : s.store_id}>{s.name}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12, color: "#374151", flex: isMobile ? "1 1 46%" : "0 0 auto" }}>
          Marketplace
          <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)}
            style={{ padding: "8px 9px", border: "1px solid #d1d5db", borderRadius: 6, marginTop: 3, width: "100%", minWidth: isMobile ? 0 : 150 }}>
            {(data?.marketplaces_filter || [{ id: "all", name: "All marketplaces" }]).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>
        <div style={{ display: "flex", flexDirection: "column", fontSize: 12, color: "#374151", flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
          Show
          <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
            {SHOW.map((o) => (
              <button key={o.key} type="button" onClick={() => setShow(o.key)}
                style={{
                  padding: "8px 11px", borderRadius: 6, fontSize: 13, cursor: "pointer", flex: isMobile ? 1 : "0 0 auto",
                  border: "1px solid " + (show === o.key ? "#2563eb" : "#d1d5db"),
                  background: show === o.key ? "#2563eb" : "#fff",
                  color: show === o.key ? "#fff" : "#374151",
                }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <button type="button" onClick={load}
          style={{ padding: "9px 16px", borderRadius: 6, border: "1px solid #2563eb", background: "#fff", color: "#2563eb", cursor: "pointer", flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
          Refresh
        </button>
      </div>

      {err && <div style={{ color: "#b91c1c", marginBottom: 12 }}>{err}</div>}
      {loading && <Loader />}

      {!loading && data && (
        <>
          {/* View A — summary (cards on mobile, table on desktop) */}
          <Section title="Slot summary">
            {isMobile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {mpGroups.map((grp) => (
                  <React.Fragment key={grp.name}>
                    {multiMp && (
                      <div style={{ background: mpColor(grp.name), color: "#fff", borderRadius: 10, padding: "10px 12px", marginTop: 4 }}>
                        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>{grp.name}</div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <div style={{ flex: 1, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 18 }}>{wantP ? grp.p : "—"}</div><div style={{ fontSize: 10, opacity: 0.85 }}>Pickups</div></div>
                          <div style={{ flex: 1, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 18 }}>{wantD ? grp.d : "—"}</div><div style={{ fontSize: 10, opacity: 0.85 }}>Deliveries</div></div>
                          <div style={{ flex: 1, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 18 }}>{grp.t}</div><div style={{ fontSize: 10, opacity: 0.85 }}>Total</div></div>
                        </div>
                      </div>
                    )}
                    {grp.rows.map((s) => {
                      const busiest = data.busiest && s.presentation === data.busiest;
                      const open = expanded === s.presentation;
                      const stat = (label, val, color, onClick) => (
                        <div style={{ flex: 1, textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>{label}</div>
                          <div style={{ fontSize: 19, fontWeight: 700, color, lineHeight: 1.3 }}>
                            {onClick ? numBtn(val, onClick, true) : val}
                          </div>
                        </div>
                      );
                      return (
                        <div key={s.presentation}
                          style={{ border: "1px solid " + (busiest ? "#bfdbfe" : s.full ? "#fed7aa" : "#e5e7eb"), borderRadius: 10, padding: 12, background: busiest ? "#eff6ff" : s.full ? "#fff7ed" : "#fff" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>{s.presentation}</div>
                            <div>
                              {busiest && <span style={badge("#2563eb")}>busiest</span>}
                              {s.full && <span style={badge("#ea580c")}>full</span>}
                            </div>
                          </div>
                          <div style={{ display: "flex", marginTop: 10, gap: 4 }}>
                            {wantP && stat("Pickups", s.pickups, PICK, s.pickups ? () => openDrill(s, "pickup", null, `Pickups · ${s.presentation}`) : null)}
                            {wantD && stat("Deliveries", s.deliveries, DROP, s.deliveries ? () => openDrill(s, "delivery", null, `Deliveries · ${s.presentation}`) : null)}
                            {stat("Total", s.total, "#111827", null)}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontSize: 12 }}>
                            <span style={{ color: s.full ? "#ea580c" : "#6b7280" }}>
                              {s.capacity > 0 ? `Used ${s.used} / ${s.capacity}` : "—"}
                            </span>
                            {s.types.length > 0 && (
                              <button type="button" onClick={() => setExpanded(open ? null : s.presentation)}
                                style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", fontSize: 13, padding: 0 }}>
                                {open ? "Hide types ▾" : "Order types ▸"}
                              </button>
                            )}
                          </div>
                          {open && s.types.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                              {s.types.map((t) => (
                                <span key={t.type} style={{ fontSize: 12, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px" }}>
                                  <b>{t.label}</b> {t.total}
                                  <span style={{ color: "#9ca3af" }}>
                                    {" ("}
                                    {wantP && <span style={{ color: PICK }}>P {t.pickups}</span>}
                                    {wantP && wantD && "/"}
                                    {wantD && <span style={{ color: DROP }}>D {t.deliveries}</span>}
                                    {")"}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                {/* Grand total — quiet neutral sum below all sections. */}
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 12px", color: "#6b7280", fontSize: 12, borderTop: "1px solid #e5e7eb" }}>
                  <span style={{ flex: "1 1 auto" }}>Grand total</span>
                  <span>P {wantP ? totals.pickups : "—"}</span>
                  <span>D {wantD ? totals.deliveries : "—"}</span>
                  <span style={{ fontWeight: 700, color: "#374151" }}>Total {totals.total}</span>
                </div>
              </div>
            ) : (
            <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
              <table className="table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                    <th style={th}>Time Slot</th>
                    {wantP && <th style={thNum}>Pickups</th>}
                    {wantD && <th style={thNum}>Deliveries</th>}
                    <th style={thNum}>Total</th>
                    <th style={thNum}>Used / Limit</th>
                  </tr>
                </thead>
                <tbody>
                  {mpGroups.map((grp) => {
                    const colSpan = 2 + (wantP ? 1 : 0) + (wantD ? 1 : 0);
                    return (
                      <React.Fragment key={grp.name}>
                        {multiMp && (
                          <tr>
                            <td colSpan={colSpan + 1} style={{ padding: "8px 0 4px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 20, background: mpColor(grp.name), color: "#fff", padding: "9px 16px", borderRadius: 8, flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 800, fontSize: 16, flex: "1 1 auto" }}>{grp.name}</span>
                                {wantP && <span><b style={{ fontSize: 19 }}>{grp.p}</b> <span style={{ opacity: 0.85, fontSize: 12 }}>Pickups</span></span>}
                                {wantD && <span><b style={{ fontSize: 19 }}>{grp.d}</b> <span style={{ opacity: 0.85, fontSize: 12 }}>Deliveries</span></span>}
                                <span><b style={{ fontSize: 19 }}>{grp.t}</b> <span style={{ opacity: 0.85, fontSize: 12 }}>Total</span></span>
                              </div>
                            </td>
                          </tr>
                        )}
                        {grp.rows.map((s) => {
                          const busiest = data.busiest && s.presentation === data.busiest;
                          return (
                            <React.Fragment key={s.presentation}>
                              <tr style={{ borderTop: "1px solid #eee", background: busiest ? "#eff6ff" : s.full ? "#fff7ed" : "transparent" }}>
                                <td style={td}>
                                  <button type="button" onClick={() => setExpanded(expanded === s.presentation ? null : s.presentation)}
                                    style={{ background: "none", border: "none", cursor: "pointer", fontWeight: 600, color: "#111827", padding: 0, fontSize: 14 }}>
                                    {expanded === s.presentation ? "▾ " : "▸ "}{s.presentation}
                                  </button>
                                  {busiest && <span style={badge("#2563eb")}>busiest</span>}
                                  {s.full && <span style={badge("#ea580c")}>full</span>}
                                </td>
                                {wantP && <td style={tdNum}>{numBtn(s.pickups, () => openDrill(s, "pickup", null, `Pickups · ${s.presentation}`))}</td>}
                                {wantD && <td style={tdNum}>{numBtn(s.deliveries, () => openDrill(s, "delivery", null, `Deliveries · ${s.presentation}`))}</td>}
                                <td style={{ ...tdNum, fontWeight: 700 }}>{s.total}</td>
                                <td style={{ ...tdNum, color: s.full ? "#ea580c" : "#6b7280" }}>
                                  {s.capacity > 0 ? `${s.used} / ${s.capacity}` : "—"}
                                </td>
                              </tr>
                              {expanded === s.presentation && s.types.length > 0 && (
                                <tr>
                                  <td colSpan={colSpan} style={{ padding: "4px 14px 12px", background: "#fafafa" }}>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                      {s.types.map((t) => (
                                        <span key={t.type} style={{ fontSize: 13, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 9px" }}>
                                          <b>{t.label}</b> {t.total}
                                          <span style={{ color: "#9ca3af" }}>
                                            {" ("}
                                            {wantP && <span style={{ color: PICK }}>P {t.pickups}</span>}
                                            {wantP && wantD && " / "}
                                            {wantD && <span style={{ color: DROP }}>D {t.deliveries}</span>}
                                            {")"}
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {/* Grand total — quiet neutral sum below all sections. */}
                  <tr style={{ borderTop: "1px solid #e5e7eb", color: "#6b7280", fontSize: 12 }}>
                    <td style={td}>Grand total</td>
                    {wantP && <td style={tdNum}>{totals.pickups}</td>}
                    {wantD && <td style={tdNum}>{totals.deliveries}</td>}
                    <td style={{ ...tdNum, fontWeight: 700, color: "#374151" }}>{totals.total}</td>
                    <td style={tdNum} />
                  </tr>
                </tbody>
              </table>
            </div>
            )}
          </Section>

          {/* Rescheduled today */}
          {data.rescheduled && (
            <Section title={`Rescheduled on this day — ${data.rescheduled.count} (${data.rescheduled.rate}% of ${data.rescheduled.day_orders})`}>
              {data.rescheduled.orders.length === 0 ? (
                <div style={{ color: "#6b7280", fontSize: 13 }}>No orders were rescheduled on this day.</div>
              ) : (
                <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 420 }}>
                    <thead>
                      <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                        <th style={th}>Order</th><th style={th}>Customer</th><th style={th}>Type</th><th style={th}>Driver</th><th style={th}>Status</th><th style={th}>Rescheduled at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rescheduled.orders.map((o) => (
                        <tr key={o.order_id} style={{ borderTop: "1px solid #eee" }}>
                          <td style={td}><a href={`/orders/${o.order_id}`} style={{ color: "#2563eb" }}>{o.order_id}</a></td>
                          <td style={td}>{o.customer || "—"}</td>
                          <td style={td}>{o.order_type}</td>
                          <td style={td}>{o.driver || "-"}</td>
                          <td style={td}>{o.status}</td>
                          <td style={td}>{o.rescheduled_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}

          {/* Reschedules per user */}
          {data.reschedules_by_user && data.reschedules_by_user.rows.length > 0 && (
            <Section title={`Reschedules per user — ${data.reschedules_by_user.totals.reschedules} of ${data.reschedules_by_user.totals.orders} (${data.reschedules_by_user.totals.rate}%)`}>
              <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 460 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                      <th style={th}>User</th><th style={th}>Group</th><th style={thNum}>Orders handled</th><th style={thNum}>Reschedules</th><th style={thNum}>Reschedule %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reschedules_by_user.rows.map((r) => (
                      <React.Fragment key={r.user}>
                        <tr style={{ borderTop: "1px solid #eee" }}>
                          <td style={td}>
                            <button type="button" onClick={() => setExpandedUser(expandedUser === r.user ? null : r.user)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#111827", padding: 0, fontSize: 13, fontWeight: 600 }}>
                              {expandedUser === r.user ? "▾ " : "▸ "}{r.user}
                            </button>
                          </td>
                          <td style={td}>{r.group}</td>
                          <td style={tdNum}>{r.orders_handled}</td>
                          <td style={tdNum}>{r.reschedules}</td>
                          <td style={{ ...tdNum, color: r.reschedule_rate >= 10 ? "#ea580c" : "#374151" }}>{r.reschedule_rate}%</td>
                        </tr>
                        {expandedUser === r.user && (
                          <tr>
                            <td colSpan={5} style={{ padding: "4px 12px 12px", background: "#fafafa" }}>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {r.transitions.map((t, i) => (
                                  <span key={i} style={{ display: "inline-block", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "2px 7px", margin: 2 }}>
                                    <a href={`/orders/${t.order_id}`} style={{ color: "#2563eb" }}>{t.order_id}</a> {t.from}→{t.to} <span style={{ color: "#9ca3af" }}>{t.at.slice(11, 16)}</span>
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    <tr style={{ borderTop: "2px solid #d1d5db", background: "#f9fafb", fontWeight: 700 }}>
                      <td style={td}>Total</td><td style={td} />
                      <td style={tdNum}>{data.reschedules_by_user.totals.orders}</td>
                      <td style={tdNum}>{data.reschedules_by_user.totals.reschedules}</td>
                      <td style={tdNum}>{data.reschedules_by_user.totals.rate}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>{data.reschedules_by_user.note}</div>
            </Section>
          )}

          {/* View B — load bars */}
          <Section title="Load by slot">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {slots.map((s) => (
                <div key={s.presentation}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
                    <span>{s.presentation}</span>
                    <span style={{ color: "#6b7280" }}>{s.total}</span>
                  </div>
                  <div style={{ display: "flex", height: 16, borderRadius: 4, overflow: "hidden", background: "#f3f4f6" }}>
                    {wantP && (
                      <div title={`Pickups ${s.pickups}`} style={{ width: `${(s.pickups / maxTotal) * 100}%`, background: PICK }} />
                    )}
                    {wantD && (
                      <div title={`Deliveries ${s.deliveries}`} style={{ width: `${(s.deliveries / maxTotal) * 100}%`, background: DROP }} />
                    )}
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {wantP && <span><span style={swatch(PICK)} /> Pickups</span>}
                {wantD && <span><span style={swatch(DROP)} /> Deliveries</span>}
              </div>
            </div>
          </Section>

          {/* View D — matrix slot x type */}
          {types.length > 0 && (
            <Section title="Slot × order type">
              <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 13, minWidth: 520 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      <th style={{ ...th, position: "sticky", left: 0, background: "#f9fafb", zIndex: 1 }}>Time Slot</th>
                      {types.map((t) => <th key={t.type} style={thNum}>{t.label}</th>)}
                      <th style={thNum}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map((s) => {
                      const byType = Object.fromEntries(s.types.map((t) => [t.type, t]));
                      return (
                        <tr key={s.presentation} style={{ borderTop: "1px solid #eee" }}>
                          <td style={{ ...td, position: "sticky", left: 0, background: "#fff", fontWeight: 600 }}>{s.presentation}</td>
                          {types.map((t) => {
                            const cell = byType[t.type];
                            const v = cell ? cell.total : 0;
                            return (
                              <td key={t.type} style={tdNum}>
                                {v ? numBtn(v, () => openDrill(s, show === "both" ? "both" : show, t.type, `${t.label} · ${s.presentation}`)) : <span style={{ color: "#d1d5db" }}>0</span>}
                              </td>
                            );
                          })}
                          <td style={{ ...tdNum, fontWeight: 700 }}>{s.total}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: "2px solid #d1d5db", background: "#f9fafb", fontWeight: 700 }}>
                      <td style={{ ...td, position: "sticky", left: 0, background: "#f9fafb" }}>Total</td>
                      {types.map((t) => {
                        const col = slots.reduce((a, s) => a + (s.types.find((x) => x.type === t.type)?.total || 0), 0);
                        return <td key={t.type} style={tdNum}>{col}</td>;
                      })}
                      <td style={tdNum}>{totals.total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* View E — next 7 days heatmap */}
          {data.heatmap && (
            <Section title="Next 7 days">
              <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>Time Slot</th>
                      {data.heatmap.days.map((d) => (
                        <th key={d} style={{ ...thNum, whiteSpace: "nowrap" }}>{d.slice(5)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const gridMax = Math.max(1, ...data.heatmap.rows.flatMap((r) => r.cells));
                      return data.heatmap.rows.map((r) => (
                        <tr key={r.presentation}>
                          <td style={{ ...td, position: "sticky", left: 0, background: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}>{r.presentation}</td>
                          {r.cells.map((c, i) => (
                            <td key={i} style={{ textAlign: "center", padding: "8px 10px", background: heatColor(c, gridMax), color: c / gridMax > 0.6 ? "#fff" : "#374151" }}>
                              {c || ""}
                            </td>
                          ))}
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </>
      )}

      {/* Drill-down modal */}
      {drill && (
        <div onClick={() => setDrill(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 1000, padding: isMobile ? 0 : 12 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: isMobile ? "14px 14px 0 0" : 10, maxWidth: 760, width: "100%", maxHeight: isMobile ? "92vh" : "85vh", overflow: "auto", WebkitOverflowScrolling: "touch", padding: isMobile ? 14 : 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 17 }}>{drill.label}</h3>
              <button type="button" onClick={() => setDrill(null)} style={{ border: "none", background: "none", fontSize: 22, cursor: "pointer", color: "#6b7280" }}>×</button>
            </div>
            {drillLoading && <Loader />}
            {!drillLoading && drillData && (
              <>
                <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}>{drillData.count} order(s)</div>
                <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                        <th style={th}>Order</th><th style={th}>Customer</th><th style={th}>Store</th><th style={th}>Type</th>
                        {drill.leg === "both" && <th style={th}>Leg</th>}
                        <th style={th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillData.orders.map((o, i) => (
                        <tr key={`${o.order_id}-${o.leg}-${i}`} style={{ borderTop: "1px solid #eee" }}>
                          <td style={td}><a href={`/orders/${o.order_id}`} style={{ color: "#2563eb" }}>{o.order_id}</a></td>
                          <td style={td}>{o.customer || "—"}</td>
                          <td style={td}>{o.store_id}</td>
                          <td style={td}>{o.order_type}</td>
                          {drill.leg === "both" && <td style={{ ...td, color: o.leg === "Pickup" ? PICK : DROP }}>{o.leg}</td>}
                          <td style={td}>{o.status}</td>
                        </tr>
                      ))}
                      {drillData.orders.length === 0 && (
                        <tr><td style={td} colSpan={drill.leg === "both" ? 6 : 5}>No orders.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <h3 style={{ fontSize: 15, margin: "0 0 10px", color: "#374151" }}>{title}</h3>
      {children}
    </div>
  );
}

const th = { padding: "9px 12px", fontSize: 12, color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" };
const thNum = { ...th, textAlign: "right" };
const td = { padding: "9px 12px", verticalAlign: "top" };
const tdNum = { ...td, textAlign: "right" };
const badge = (c) => ({ marginLeft: 8, fontSize: 11, color: "#fff", background: c, borderRadius: 4, padding: "1px 6px" });
const swatch = (c) => ({ display: "inline-block", width: 10, height: 10, background: c, borderRadius: 2, marginRight: 4 });
